// Exceptions that reduce what a patient owes or give money back: waivers, discounts,
// credit notes and refunds (docs/plans/phase1-billing.md §5.5; CLAUDE.md domain rule 10).
//
// Each needs a permission (checked by the API), a reason, and a decision by a different
// person. The segregation rule and the amount limits live here, once, so the three kinds
// of request can't drift apart.

import type { BasisPoints, Money } from '../money.ts';
import {
  applyDiscount,
  minMoney,
  ONE_HUNDRED_PERCENT,
  subtractMoney,
  zeroMoney,
} from '../money.ts';
import type { Result } from '../result.ts';
import { err, ok } from '../result.ts';
import type { ChargeStatus, PricingState } from './charge.ts';

// --- Decisions ------------------------------------------------------------------------

export const REQUEST_STATUSES = ['requested', 'approved', 'rejected', 'withdrawn'] as const;
export type RequestStatus = (typeof REQUEST_STATUSES)[number];

export type RequestAction = 'approve' | 'reject' | 'withdraw';

export type ApprovalErrorCode =
  | 'approval.approver_is_requester'
  | 'approval.already_decided'
  | 'approval.not_requester'
  | 'approval.note_required';

/** English descriptions; the API and the web app own the wording shown to a user. */
export const APPROVAL_ERRORS: Record<ApprovalErrorCode, string> = {
  'approval.approver_is_requester':
    'You asked for this, so someone else with approval rights must decide it',
  'approval.already_decided': 'This request has already been decided or withdrawn',
  'approval.not_requester': 'Only the person who asked can withdraw this request',
  'approval.note_required': 'Say why you are rejecting this request',
};

/**
 * Decides a request. Approving and rejecting are for someone other than the requester,
 * always, even in a one-cashier clinic (plan §13 decision 7); a rejection says why.
 * Withdrawing is only for the requester. A request is decided once.
 */
export function decideRequest(input: {
  readonly status: RequestStatus;
  readonly requestedBy: string;
  readonly actorId: string;
  readonly action: RequestAction;
  readonly note?: string | null | undefined;
}): Result<RequestStatus, ApprovalErrorCode> {
  if (input.status !== 'requested') return err('approval.already_decided');
  switch (input.action) {
    case 'withdraw':
      if (input.actorId !== input.requestedBy) return err('approval.not_requester');
      return ok('withdrawn');
    case 'approve':
      if (input.actorId === input.requestedBy) return err('approval.approver_is_requester');
      return ok('approved');
    case 'reject':
      if (input.actorId === input.requestedBy) return err('approval.approver_is_requester');
      if ((input.note ?? '').trim() === '') return err('approval.note_required');
      return ok('rejected');
  }
}

// --- Reasons ----------------------------------------------------------------------------
//
// Approved lists (plan §13 decision 8). `other` always needs a note.

export const WAIVER_REASONS = [
  'hardship',
  'staff',
  'management_decision',
  'service_complaint',
  'other',
] as const;
export type WaiverReason = (typeof WAIVER_REASONS)[number];

export const CREDIT_NOTE_REASONS = [
  'service_not_provided',
  'charged_in_error',
  'wrong_quantity',
  'other',
] as const;
export type CreditNoteReason = (typeof CREDIT_NOTE_REASONS)[number];

export const REFUND_REASONS = [
  'service_not_provided',
  'overpayment',
  'charged_in_error',
  'other',
] as const;
export type RefundReason = (typeof REFUND_REASONS)[number];

export type ReasonErrorCode = 'reason.note_required';

export function checkReason(code: string, note?: string | null): Result<null, ReasonErrorCode> {
  if (code === 'other' && (note ?? '').trim() === '') return err('reason.note_required');
  return ok(null);
}

// --- Waivers and discounts ----------------------------------------------------------------

export type AdjustmentTerms =
  | { readonly basis: 'percent'; readonly rate: BasisPoints }
  | { readonly basis: 'amount'; readonly amount: Money };

export type AdjustmentErrorCode =
  | 'adjustment.charge_not_pending'
  | 'adjustment.charge_not_priced'
  | 'adjustment.invalid_rate'
  | 'adjustment.not_positive'
  | 'adjustment.exceeds_share';

export const ADJUSTMENT_ERRORS: Record<AdjustmentErrorCode, string> = {
  'adjustment.charge_not_pending':
    'Only a charge that is not yet on a bill can be waived or discounted. Ask for a credit note instead',
  'adjustment.charge_not_priced': 'This charge has no price yet',
  'adjustment.invalid_rate': 'A percentage must be more than 0 and at most 100',
  'adjustment.not_positive': 'The amount to take off must be more than zero',
  'adjustment.exceeds_share': 'That is more than the patient still has to pay for this charge',
};

/**
 * What a waiver or discount takes off the patient's share of one charge. A percentage is
 * of the patient's share as charged, rounded by `applyDiscount` (ADR 0006). Nothing can
 * take off more than is left after earlier approved adjustments, and the payer's share is
 * never touched: disputes with a payer are claims work.
 */
export function adjustmentAmount(input: {
  readonly terms: AdjustmentTerms;
  readonly chargeStatus: ChargeStatus;
  readonly pricing: PricingState;
  readonly patientShare: Money;
  readonly alreadyAdjusted: Money;
}): Result<Money, AdjustmentErrorCode> {
  if (input.chargeStatus !== 'pending') return err('adjustment.charge_not_pending');
  if (input.pricing !== 'priced') return err('adjustment.charge_not_priced');
  let amount: Money;
  if (input.terms.basis === 'percent') {
    const rate = input.terms.rate;
    if (!Number.isInteger(rate) || rate <= 0 || rate > ONE_HUNDRED_PERCENT)
      return err('adjustment.invalid_rate');
    amount = applyDiscount(input.patientShare, rate).discount;
  } else {
    amount = input.terms.amount;
  }
  if (amount.cents <= 0n) return err('adjustment.not_positive');
  const remaining = subtractMoney(input.patientShare, input.alreadyAdjusted);
  if (amount.cents > remaining.cents) return err('adjustment.exceeds_share');
  return ok(amount);
}

// --- Credit notes and refunds ----------------------------------------------------------------

export type CreditErrorCode = 'credit.not_positive' | 'credit.exceeds_line';

export const CREDIT_ERRORS: Record<CreditErrorCode, string> = {
  'credit.not_positive': 'The amount to credit must be more than zero',
  'credit.exceeds_line': 'That is more than is left to credit on this line',
};

/** What is left to credit on an invoice line after earlier approved credits. */
export function creditableAmount(lineAmount: Money, priorCredits: Money): Money {
  const left = subtractMoney(lineAmount, priorCredits);
  if (left.cents < 0n) throw new RangeError('A line has been credited more than its amount');
  return left;
}

export function checkCredit(input: {
  readonly amount: Money;
  readonly lineAmount: Money;
  readonly priorCredits: Money;
}): Result<Money, CreditErrorCode> {
  if (input.amount.cents <= 0n) return err('credit.not_positive');
  if (input.amount.cents > creditableAmount(input.lineAmount, input.priorCredits).cents)
    return err('credit.exceeds_line');
  return ok(input.amount);
}

export type RefundErrorCode = 'refund.not_positive' | 'refund.exceeds_refundable';

export const REFUND_ERRORS: Record<RefundErrorCode, string> = {
  'refund.not_positive': 'The amount to refund must be more than zero',
  'refund.exceeds_refundable':
    'That is more than can be refunded: the patient has less in credit, or this payment has less left',
};

/**
 * The most that can be refunded from one payment: what the payment has left after earlier
 * refunds, and never more than the patient has in credit on the visit, so a refund can't
 * make the patient owe for something they have already been given back.
 */
export function refundableAmount(input: {
  readonly patientCredit: Money;
  readonly paymentAmount: Money;
  readonly priorRefunds: Money;
}): Money {
  const leftOnPayment = subtractMoney(input.paymentAmount, input.priorRefunds);
  if (leftOnPayment.cents < 0n)
    throw new RangeError('A payment has been refunded more than its amount');
  const capped = minMoney(leftOnPayment, input.patientCredit);
  return capped.cents < 0n ? zeroMoney(capped.currency) : capped;
}

export function checkRefund(input: {
  readonly amount: Money;
  readonly patientCredit: Money;
  readonly paymentAmount: Money;
  readonly priorRefunds: Money;
}): Result<Money, RefundErrorCode> {
  if (input.amount.cents <= 0n) return err('refund.not_positive');
  if (input.amount.cents > refundableAmount(input).cents) return err('refund.exceeds_refundable');
  return ok(input.amount);
}
